// @ts-nocheck
/* eslint-disable react/prop-types */
import { useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputBase from '@mui/material/InputBase';
import InputAdornment from '@mui/material/InputAdornment';
import { ReactComponent as KeyboardArrowUpIcon } from '../../assets/KeyboardArrowUpIcon.svg';
import { ReactComponent as KeyboardArrowDownIcon } from '../../assets/KeyboardArrowDownIcon.svg';
import { alpha } from '@mui/material/styles';

/**
 * NumberField - A numeric input with increment/decrement buttons
 *
 * @component
 * @description
 * NumberField provides a text-based numeric input with up/down arrow buttons,
 * min/max constraints, step increments, and character count display. Used
 * internally by SolyticsInput when type="number".
 *
 * Uses string-based handling with BigInt arithmetic under the hood, so it
 * correctly supports Int, Float, BigInt, and BigFloat values without
 * JavaScript Number precision loss.
 *
 * **Important:** For values that exceed `Number.MAX_SAFE_INTEGER`
 * (9007199254740991) or require high decimal precision, pass `value` as a
 * **string** (e.g. `"9223372036854775800"` or `"123.98765432109876"`).
 * The component emits string values in `onChange`.
 *
 * @param {string} id - HTML id attribute
 * @param {string} name - Name attribute
 * @param {number|string} value - Current value (string for big / high-precision numbers)
 * @param {function} onChange - Change handler; receives { target: { name, value: string } }
 * @param {boolean} [error=false] - If true, shows error styling
 * @param {React.ReactNode} helperText - Helper text below the input
 * @param {boolean} [disabled=false] - If true, disables the input
 * @param {boolean} [readOnly=false] - If true, makes the input read-only
 * @param {boolean} [required=false] - If true, marks as required
 * @param {string} [variant='standard'] - Visual variant
 * @param {string} [size='small'] - Size of increment/decrement buttons
 * @param {boolean} [fullWidth=false] - If true, takes full width
 * @param {string} placeholder - Placeholder text
 * @param {React.ReactNode} startAdornment - Start adornment element
 * @param {React.ReactNode} endAdornment - End adornment element (replaces arrow buttons)
 * @param {boolean} [showCharCount=false] - If true, shows character count
 * @param {number} maxCharacterAllowed - Maximum character limit
 * @param {number|string} min - Minimum allowed value
 * @param {number|string} max - Maximum allowed value
 * @param {number} [step=1] - Step increment for arrow buttons
 * @param {number|string} defaultValue - Default uncontrolled value (unused in controlled mode)
 * @param {boolean} [allowDecimal=true] - If false, only allows integer input
 * @param {function} onFocus - Focus event handler
 * @param {function} onBlur - Blur event handler
 * @param {boolean} [autoFocus=false] - If true, auto-focuses on mount
 * @param {Object} sx - Additional sx styles to merge
 *
 * @returns {React.ReactElement} A number input with increment/decrement controls
 */

/* ------------------ DEFAULT CONSTANTS ------------------ */
const DEFAULT_VARIANT = 'standard';
const DEFAULT_SIZE = 'small';
const DEFAULT_WIDTH = '280px';

/* ------------------ THEME TOKENS ------------------ */
const getThemeTokens = (theme) => ({
  focusBorderColor:
    theme.palette.lavenderBlue?.[80] || theme.palette.primary?.main,
  defaultBorderColor: theme.palette.divider || theme.palette.grey?.[60],
  backgroundColor: theme.palette.background?.default,
  focusedBackgroundColor: theme.palette.background?.paper || '#FFFFFF',
  textColor: theme.palette.text?.secondary,
  placeholderColor: theme.palette.text?.primary,
  disabledPlaceholderColor: theme.palette.text?.disabled,
  labelColor: theme.palette.text?.secondary,
  helperTextColor: theme.palette.text?.secondary,
  errorColor: theme.palette.error?.main,
  characterCountColor: theme.palette.grey?.T30,
  focusBoxShadowColor: alpha(
    theme.palette.lavenderBlue?.[20] || theme.palette.primary?.light,
    0.5
  ),
});

/* ------------------ STYLE GENERATORS ------------------ */
const NUMBER_SIZE_PADDING = {
  small: '6px 12px',
  medium: '8px 12px',
};

const INPUT_FIELD_BORDER_RADIUS = 0.5;

const getInputBaseStyles = (
  theme,
  { error, disabled, readOnly, fullWidth, size }
) => {
  const tokens = getThemeTokens(theme);

  return {
    width: fullWidth ? '100%' : DEFAULT_WIDTH,
    borderRadius: INPUT_FIELD_BORDER_RADIUS,
    backgroundColor: tokens.backgroundColor,
    border: '1px solid',
    borderColor: error ? tokens.errorColor : tokens.defaultBorderColor,
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    '&:hover': {
      borderColor: (() => {
        if (disabled) return tokens.defaultBorderColor;
        if (error) return tokens.errorColor;
        return tokens.focusBorderColor;
      })(),
    },
    '&.Mui-focused': {
      backgroundColor: tokens.focusedBackgroundColor,
      boxShadow: error
        ? `${alpha(tokens.errorColor, 0.1)} 0 0 0 0.2rem`
        : `${tokens.focusBoxShadowColor} 0 0 0 0.2rem`,
      borderColor: error ? tokens.errorColor : tokens.focusBorderColor,
    },
    '&.Mui-disabled': {
      opacity: 0.6,
    },
    '& .MuiInputAdornment-root': {
      color: tokens.placeholderColor,
      '&.MuiInputAdornment-positionStart': {
        marginRight: 0,
      },
      '&.MuiInputAdornment-positionEnd': {
        marginLeft: 0,
      },
    },
    '& .MuiInputBase-input': {
      fontSize: 14,
      padding: NUMBER_SIZE_PADDING[size] || NUMBER_SIZE_PADDING.medium,
      paddingRight: '48px',
      fontFamily: theme.typography.fontFamily,
      color: tokens.textColor,
      border: 'none',
      backgroundColor: 'transparent',
      cursor: disabled ? 'not-allowed' : readOnly ? 'default' : 'text',
      '&::placeholder': {
        color: disabled
          ? tokens.disabledPlaceholderColor
          : tokens.placeholderColor,
        opacity: 1,
      },
      '&:focus': {
        outline: 0,
      },
      '&:disabled': {
        cursor: 'not-allowed',
      },
    },
  };
};

/* ---------- PRECISION ARITHMETIC (BigInt-scaled) ---------- */

const PARTIAL_INT = /^-?\d*$/;
const PARTIAL_DECIMAL = /^-?\d*\.?\d*$/;

const decPlaces = (s) => {
  const i = s.indexOf('.');
  return i >= 0 ? s.length - i - 1 : 0;
};

const toBig = (s, d) => {
  const neg = s.startsWith('-');
  const abs = neg ? s.slice(1) : s;
  const [int = '0', dec = ''] = abs.split('.');
  const n = BigInt(int + dec.padEnd(d, '0'));
  return neg ? -n : n;
};

const fromBig = (n, d) => {
  if (d === 0) return n.toString();
  const neg = n < 0n;
  const abs = (neg ? -n : n).toString().padStart(d + 1, '0');
  const dec = abs.slice(-d).replace(/0+$/, '');
  return (neg ? '-' : '') + abs.slice(0, -d) + (dec ? '.' + dec : '');
};

const compareBig = (a, b) => {
  try {
    const d = Math.max(decPlaces(a), decPlaces(b));
    const diff = toBig(a, d) - toBig(b, d);
    if (diff < 0n) return -1;
    if (diff > 0n) return 1;
    return 0;
  } catch {
    return 0;
  }
};

const stepStr = (n) => {
  const s = String(n);
  return /[eE]/.test(s)
    ? n.toFixed(20).replace(/0+$/, '').replace(/\.$/, '')
    : s;
};

const steppedValue = (cur, step = 1, dir = 1) => {
  const ss = stepStr(step);
  if (!cur || cur === '-') return dir === -1 ? '-' + ss : ss;
  const d = Math.max(decPlaces(cur), decPlaces(ss));
  return fromBig(toBig(cur, d) + BigInt(dir) * toBig(ss, d), d);
};

function NumberField({
  id,
  name,
  value,
  onChange,
  error = false,
  helperText,
  disabled = false,
  readOnly = false,
  required = false,
  variant = DEFAULT_VARIANT,
  size = DEFAULT_SIZE,
  fullWidth = false,
  placeholder,
  startAdornment,
  endAdornment,
  showCharCount = false,
  maxCharacterAllowed,
  min,
  max,
  step = 1,
  allowDecimal = true,
  onFocus,
  onBlur,
  autoFocus = false,
  disableScrollChange = false,
  sx,
}) {
  /* ------------------ DERIVED VALUES ------------------ */
  const displayValue = value != null ? String(value) : '';
  const showCount = showCharCount && maxCharacterAllowed;
  const inputId = id || `${name}-input`;
  const helperId =
    helperText || showCount ? `${inputId}-helper-text` : undefined;

  /* ------------------ CALLBACKS ------------------ */

  const emit = useCallback(
    (v) => onChange?.({ target: { name, value: v } }),
    [onChange, name]
  );

  const handleInputChange = useCallback(
    (e) => {
      const raw = e.target.value;
      if (
        raw !== '' &&
        raw !== '-' &&
        !(allowDecimal ? PARTIAL_DECIMAL : PARTIAL_INT).test(raw)
      )
        return;
      emit(raw);
    },
    [allowDecimal, emit]
  );

  const clamp = useCallback(
    (val) => {
      if (!val || val === '-') return '';
      if (min != null && compareBig(val, String(min)) < 0) return String(min);
      if (max != null && compareBig(val, String(max)) > 0) return String(max);
      return val;
    },
    [min, max]
  );

  const handleBlur = useCallback(
    (e) => {
      let v = displayValue;
      if (/^[-.]$|^-\.$/.test(v)) v = '';
      else if (v.endsWith('.')) v = v.slice(0, -1);
      if (v && !/^-?0(\..*)?$/.test(v)) v = v.replace(/^(-?)0+(\d)/, '$1$2');
      if (v) v = clamp(v);
      if (v !== displayValue) emit(v);
      onBlur?.(e);
    },
    [displayValue, clamp, emit, onBlur]
  );

  const handleStep = useCallback(
    (dir) => {
      if (disabled || readOnly) return;
      emit(clamp(steppedValue(displayValue, step, dir)));
    },
    [displayValue, step, disabled, readOnly, clamp, emit]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        handleStep(1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleStep(-1);
      }
    },
    [handleStep]
  );

  const handleWheel = useCallback(
    (e) => {
      if (disableScrollChange) {
        e.target.blur();
      }
    },
    [disableScrollChange]
  );

  /* ------------------ RENDER ------------------ */
  return (
    <FormControl
      disabled={disabled}
      required={required}
      error={error}
      variant={variant}
      fullWidth={fullWidth}
      sx={{ width: fullWidth ? '100%' : DEFAULT_WIDTH }}
    >
      <InputBase
        id={inputId}
        name={name}
        type="text"
        inputMode={allowDecimal ? 'decimal' : 'numeric'}
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
        startAdornment={startAdornment}
        endAdornment={
          endAdornment ? (
            <>{endAdornment}</>
          ) : (
            <InputAdornment
              position="end"
              sx={() => ({
                position: 'absolute',
                right: 0,
                top: 0,
                height: '100%',
                flexDirection: 'column',
                justifyContent: 'center',
                maxHeight: 'unset',
                alignSelf: 'stretch',
                overflow: 'hidden',
                '& button': {
                  py: 1,
                  px: 0.5,
                  flex: 'none',
                  borderRadius: 0,
                  minWidth: 28,
                  height: 14,
                },
              })}
            >
              <IconButton
                size={size}
                aria-label="Increase"
                disabled={disabled || readOnly}
                onClick={() => handleStep(1)}
                tabIndex={-1}
              >
                <KeyboardArrowUpIcon
                  fontSize={size === 'small' ? 'small' : 'medium'}
                  sx={{ transform: 'translateY(1px)' }}
                  height="16px"
                  width="16px"
                />
              </IconButton>
              <IconButton
                size={size}
                aria-label="Decrease"
                disabled={disabled || readOnly}
                onClick={() => handleStep(-1)}
                tabIndex={-1}
              >
                <KeyboardArrowDownIcon
                  fontSize={size === 'small' ? 'small' : 'medium'}
                  sx={{ transform: 'translateY(-1px)' }}
                  height="16px"
                  width="16px"
                />
              </IconButton>
            </InputAdornment>
          )
        }
        slotProps={{
          input: {
            'aria-describedby': helperId,
            'aria-invalid': error,
            'aria-required': required,
            autoComplete: 'off',
          },
        }}
        sx={(theme) => ({
          ...getInputBaseStyles(theme, {
            error,
            disabled,
            readOnly,
            fullWidth,
            size,
          }),
          ...sx,
        })}
      />
    </FormControl>
  );
}

export default NumberField;

/**
 * ## Features
 * - String-based input with BigInt arithmetic — full precision for Int, Float, BigInt, and BigFloat
 * - No JavaScript Number precision loss (values beyond MAX_SAFE_INTEGER supported)
 * - Increment/decrement arrow buttons (keyboard ArrowUp/ArrowDown supported)
 * - Min/max value constraints (works with arbitrary-precision values)
 * - Step increment configuration (integer and decimal steps)
 * - `allowDecimal` prop to restrict input to integers only
 * - Character count display with limit indicator
 * - Custom start/end adornments (end adornment replaces default arrows)
 * - Automatic cleanup on blur (strips incomplete input, leading zeros)
 * - Theme-integrated focus, hover, error, and disabled states
 * - WCAG accessible with aria-label on buttons and aria-describedby on input
 *
 * ## Examples
 *
 * ```jsx
 * // Basic integer input
 * <NumberField name="quantity" value={qty} onChange={handleChange} min={0} max={100} />
 *
 * // Float with decimal step
 * <NumberField name="price" value={price} onChange={handleChange} step={0.01} placeholder="0.00" />
 *
 * // BigInt (pass value as string to avoid precision loss)
 * <NumberField name="bigId" value="9223372036854775800" onChange={handleChange} allowDecimal={false} />
 *
 * // BigFloat with high decimal precision
 * <NumberField name="precise" value="9223372036854775800.9876543" onChange={handleChange} step={0.0000001} />
 *
 * // Integer-only (no decimal point allowed)
 * <NumberField name="count" value={count} onChange={handleChange} allowDecimal={false} />
 * ```
 */
